import { FindTemporalWorkflowQueryResult } from "@ternary/api-lib/core/queries/FindTemporalWorkflowQuery";
import { TemporalWorkflow } from "@ternary/api-lib/core/types";
import { EventReporter } from "@ternary/api-lib/telemetry";
import UError from "unilib-error";
import HTTPRequestSender from "../HTTPRequestSender";

interface Config {
  devMode: boolean;
  eventReporter: EventReporter;
  temporalSender: HTTPRequestSender;
}

export interface TemporalApiClient {
  createBillingV3Workflow(params: {
    tenantID: string;
    integrationID: string;
  }): Promise<string>;
  getWorkflowByTenantID(workflowId: string): Promise<TemporalWorkflow>;
}

export default class TemporalApiClientImpl implements TemporalApiClient {
  private readonly _className = "TemporalApiClient";
  private readonly _devMode: boolean;
  private readonly _eventReporter: EventReporter;
  private readonly _temporalSender: HTTPRequestSender;

  constructor(config: Config) {
    this._devMode = config.devMode;
    this._eventReporter = config.eventReporter;
    this._temporalSender = config.temporalSender;
  }

  public async createBillingV3Workflow(params: {
    tenantID: string;
    integrationID: string;
  }): Promise<string> {
    try {
      const result = await this._temporalSender.send(`/workflows/billing`, {
        method: "POST",
        body: params,
      });
      return result as string;
    } catch (error) {
      this._eventReporter.reportError(error);
      throw new UError(`${this._className}.getWorkflowLogs/ERROR_UNEXPECTED`, {
        cause: error,
      });
    }
  }

  public async getWorkflowByTenantID(
    tenantID: string
  ): Promise<TemporalWorkflow> {
    try {
      const result = (await this._temporalSender.send(
        `/workflows/billing/${tenantID}`
      )) as FindTemporalWorkflowQueryResult;
      if (!result.workflow) {
        throw new UError(`${this._className}.getWorkflow/ERROR_NOT_FOUND`, {
          cause: new Error(`Workflow not found for tenant ID: ${tenantID}`),
        });
      }
      return result.workflow;
    } catch (error) {
      this._eventReporter.reportError(error);
      throw new UError(`${this._className}.getWorkflow/ERROR_UNEXPECTED`, {
        cause: error,
      });
    }
  }
}
